import React, { useMemo, useState } from 'react';
import { Code2, Copy, Check } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Button } from '../components/ui/button';
import { toast } from '../components/ui/use-toast';
import { API_BASE_URL } from '../config/api';

type Props = {
  open: boolean;
  onOpenChange: (v: boolean) => void;
  formId: number | null;
  formTitle?: string;
};

type ApiSnippet = {
  title: string;
  note: string;
  method: string;
  path: string;
  headers: string[];
  body?: string;
};

function buildSnippets(formId: number, apiBase: string): ApiSnippet[] {
  const base = apiBase.replace(/\/+$/, '');
  return [
    {
      title: '1. Get the form',
      note: 'Staff token',
      method: 'GET',
      path: `${base}/pre-chat-forms/public/forms/${formId}`,
      headers: ['Authorization: Bearer <staff token>'],
    },
    {
      title: '2. Submit for a contact',
      note: 'Send contact_id when known; omit it to always create a new contact (client_submission_id required)',
      method: 'POST',
      path: `${base}/pre-chat-forms/public/forms/${formId}/submit`,
      headers: [
        'Authorization: Bearer <staff token>',
        'Content-Type: application/json',
      ],
      body: JSON.stringify(
        {
          answers: {
            full_name: 'Amina Otieno',
            phone: '+254 711 000 111',
          },
          contact_id: null,
          source: 'agent',
          agent_id: 5,
          client_submission_id: '<uuid generated by the app>',
        },
        null,
        2,
      ),
    },
    {
      title: '3. Has this contact filled it in?',
      note: 'Staff token',
      method: 'GET',
      path: `${base}/pre-chat-forms/${formId}/contacts/<contact_id>/status`,
      headers: ['Authorization: Bearer <staff token>'],
    },
    {
      title: '4. Re-link a submission',
      note: 'Move answers onto another contact after a conflict',
      method: 'PATCH',
      path: `${base}/pre-chat-forms/${formId}/submissions/<submission_id>`,
      headers: [
        'Authorization: Bearer <staff token>',
        'Content-Type: application/json',
      ],
      body: JSON.stringify(
        { contact_id: 312, apply_to_contact: true },
        null,
        2,
      ),
    },
    {
      title: '5. Lookup contact by phone/email',
      note: 'Before submit — cleaned E.164 / lower-cased email',
      method: 'GET',
      path: `${base}/contacts/lookup?phone=%2B254711000111`,
      headers: ['Authorization: Bearer <staff token>'],
    },
  ];
}

function snippetText(s: ApiSnippet): string {
  const lines = [`${s.method} ${s.path}`, ...s.headers];
  if (s.body) lines.push('', s.body);
  return lines.join('\n');
}

const PreChatFormApiDialog: React.FC<Props> = ({
  open,
  onOpenChange,
  formId,
  formTitle,
}) => {
  const [copiedKey, setCopiedKey] = useState<string | null>(null);

  const snippets = useMemo(
    () => (formId != null ? buildSnippets(formId, API_BASE_URL) : []),
    [formId],
  );

  const copy = async (key: string, text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setCopiedKey(key);
      toast({ title: 'Copied' });
      window.setTimeout(() => setCopiedKey(null), 2000);
    } catch {
      toast({ title: 'Could not copy', variant: 'destructive' });
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-xl max-h-[90vh] overflow-hidden flex flex-col">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Code2 className="h-4 w-4" />
            Form API
          </DialogTitle>
          <DialogDescription>
            {formTitle || 'Form'} — staff endpoints for agent submissions linked to contacts.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto space-y-3 pr-1">
          {snippets.map((s) => {
            const text = snippetText(s);
            const isCopied = copiedKey === s.title;
            return (
              <div
                key={s.title}
                className="rounded-lg border border-border bg-muted/30 overflow-hidden"
              >
                <div className="flex items-start justify-between gap-2 px-3 py-2 border-b border-border/60">
                  <div className="min-w-0">
                    <p className="text-sm font-medium leading-snug">{s.title}</p>
                    <p className="text-[11px] text-muted-foreground">{s.note}</p>
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    variant="outline"
                    className="h-7 shrink-0 gap-1 text-xs"
                    onClick={() => copy(s.title, text)}
                  >
                    {isCopied ? <Check className="h-3.5 w-3.5" /> : <Copy className="h-3.5 w-3.5" />}
                    Copy
                  </Button>
                </div>
                <pre className="px-3 py-2 text-[11px] font-mono leading-relaxed whitespace-pre-wrap break-all text-foreground/90">
                  <span className="text-sky-400">{s.method}</span> {s.path}
                  {'\n'}
                  {s.headers.join('\n')}
                  {s.body ? `\n\n${s.body}` : ''}
                </pre>
              </div>
            );
          })}

          <div className="rounded-md border border-border/60 bg-muted/20 px-3 py-2 text-[11px] text-muted-foreground space-y-1">
            <p>
              <span className="font-medium text-foreground">200</span> on submit: includes{' '}
              <code className="font-mono">contact_id</code>,{' '}
              <code className="font-mono">contact_created</code>,{' '}
              <code className="font-mono">contact_updated</code>,{' '}
              <code className="font-mono">conflicts</code>.
            </p>
            <p>
              <span className="font-medium text-foreground">422</span>:{' '}
              <code className="font-mono">{`{"details":{"errors":{"phone":"phone_needs_country_code"}}}`}</code>
            </p>
          </div>
        </div>

        <DialogFooter>
          <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
            Close
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

export default PreChatFormApiDialog;
